if (!customElements.get('aif-pdp-progress')) {
  class AifPdpProgress extends HTMLElement {
    constructor() {
      super();
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleSectionChange = this.handleSectionChange.bind(this);
      this.handleUpsellClick = this.handleUpsellClick.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.scheduleCartRefresh = this.scheduleCartRefresh.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('[data-aif-product-cro]');
      this.productInfo = this.closest('product-info');
      this.message = this.querySelector('[data-aif-progress-message]');
      this.track = this.querySelector('[data-aif-progress-track]');
      this.bar = this.querySelector('[data-aif-progress-bar]');
      this.cartTotal = Number.parseInt(this.dataset.cartTotal, 10) || 0;
      this.threshold = Number.parseInt(this.dataset.shippingThreshold, 10) || 0;
      this.variantPrice = Number.parseInt(this.dataset.variantPrice, 10) || 0;

      this.productInfo?.addEventListener('variant:change', this.handleVariantChange);
      this.section?.addEventListener('input', this.handleSectionChange);
      this.section?.addEventListener('change', this.handleSectionChange);
      this.section?.addEventListener('click', this.handleUpsellClick, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }

      this.cartDrawer = document.querySelector('cart-drawer');
      if (this.cartDrawer) {
        this.cartObserver = new MutationObserver(this.scheduleCartRefresh);
        this.cartObserver.observe(this.cartDrawer, { childList: true, subtree: true });
      }

      this.update();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('variant:change', this.handleVariantChange);
      this.section?.removeEventListener('input', this.handleSectionChange);
      this.section?.removeEventListener('change', this.handleSectionChange);
      this.section?.removeEventListener('click', this.handleUpsellClick, true);
      this.cartObserver?.disconnect();
      this.cartUpdateUnsubscriber?.();
      window.clearTimeout(this.cartRefreshTimer);
    }

    handleVariantChange(event) {
      const nextPrice = event.detail?.variant?.price;
      if (Number.isFinite(nextPrice)) this.variantPrice = nextPrice;
      this.update();
    }

    handleSectionChange(event) {
      if (event.target.matches('.cl-po--input')) {
        this.section?.querySelectorAll('.aif-has-error').forEach((element) => {
          element.classList.remove('aif-has-error');
        });
      }

      if (
        event.target.matches('[name="quantity"]')
      ) {
        window.requestAnimationFrame(() => this.update());
      }
    }

    handleUpsellClick(event) {
      const offerButton = event.target.closest?.(
        '[data-essential-upsell-element="add-to-cart-button"]'
      );
      if (!offerButton) return;

      const productForm = this.closest('form');
      const invalidField = productForm?.querySelector(':invalid')
        || this.section?.querySelector('.cl-po--input:invalid');
      if (!invalidField) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.section?.querySelectorAll('.aif-has-error').forEach((element) => {
        element.classList.remove('aif-has-error');
      });

      const invalidContainer = invalidField.closest(
        '.cl-po--wrapper, .cl-po--option, .product-form__input'
      ) || invalidField;
      invalidContainer.classList.add('aif-has-error');
      invalidContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Reuse the option app's own validation UI without allowing its bundle
      // button to bypass required personalization fields.
      const mainAddButton = productForm?.querySelector('[name="add"]');
      window.requestAnimationFrame(() => mainAddButton?.click());
    }

    handleCartUpdate(event) {
      const nextTotal = event?.cartData?.total_price;
      if (Number.isFinite(nextTotal)) {
        this.cartTotal = nextTotal;
        this.update();
        return;
      }

      this.scheduleCartRefresh();
    }

    scheduleCartRefresh() {
      window.clearTimeout(this.cartRefreshTimer);
      this.cartRefreshTimer = window.setTimeout(() => this.refreshCartTotal(), 180);
    }

    async refreshCartTotal() {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const cart = await response.json();
        if (!Number.isFinite(cart.total_price)) return;
        this.cartTotal = cart.total_price;
        this.update();
      } catch (error) {
        // The server-rendered amount remains a safe fallback if cart refresh fails.
      }
    }

    getQuantity() {
      const quantityInput = this.section?.querySelector(
        `input[name="quantity"][form="${CSS.escape(this.closest('form')?.id || '')}"], input[name="quantity"]`
      );
      return Math.max(1, Number.parseInt(quantityInput?.value, 10) || 1);
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, this.dataset.moneyFormat || '${{amount}}');
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    }

    update() {
      if (!this.threshold || !this.message || !this.track || !this.bar) return;

      const itemTotal = this.variantPrice * this.getQuantity();
      const projectedTotal = this.cartTotal + itemTotal;
      const remaining = Math.max(0, this.threshold - projectedTotal);
      const percentage = Math.min(100, (projectedTotal / this.threshold) * 100);
      const isUnlocked = projectedTotal >= this.threshold;

      if (this.cartTotal >= this.threshold) {
        this.message.textContent = this.dataset.cartUnlocked;
      } else if (isUnlocked) {
        this.message.textContent = this.dataset.itemUnlocks;
      } else {
        this.message.textContent = this.dataset.shippingRemaining.replace(
          '[amount]',
          this.formatMoney(remaining)
        );
      }

      this.classList.toggle('is-unlocked', isUnlocked);
      this.bar.style.width = `${percentage}%`;
      this.track.setAttribute('aria-valuemax', String(this.threshold / 100));
      this.track.setAttribute('aria-valuenow', String(Math.min(projectedTotal, this.threshold) / 100));
      this.track.setAttribute(
        'aria-valuetext',
        `${Math.round(percentage)} percent toward free shipping`
      );
    }
  }

  customElements.define('aif-pdp-progress', AifPdpProgress);
}
